import { Icon, Grid, Header, Divider } from 'semantic-ui-react';
import ErrorDisplay from '../ErrorDisplay/ErrorDisplay';
import { useState } from 'react';
import { observer } from 'mobx-react';
import { useHistory } from 'react-router-dom';
import axios from 'axios';
import ComingSoonBanner from '../ComingSoonBanner/ComingSoonBanner';
import InfoPopup from '../InfoPopup/InfoPopup';
import DocumentUploadModal from './DocumentUploadModal';
import ProspectPrepLoadingModal from './ProspectPrepLoadingModal';
import { meetingTypes } from './meetingTypes';

const RecordPresentationSelection = ({ meetingStore, companyStore }) => {
    const [error, setError] = useState('');
    const [showDocumentModal, setShowDocumentModal] = useState(false);
    const [prospectPrepLoading, setProspectPrepLoading] = useState(false);
    const history = useHistory();

    const onRecordingTypeClick = async (recordingType) => {
        if (recordingType === 'qa_practice') {
            // Objection Handling — go directly to standard Q&A
            try {
                await meetingStore.initQASession(companyStore.company.id, 5);
                history.push('/record-presentation/objection-handling');
            } catch (err) {
                setError(err.message || 'Failed to initialize Q&A session');
            }
        } else if (recordingType === 'deal_coaching') {
            // Document Defense — open document upload modal
            setShowDocumentModal(true);
        } else if (recordingType === 'prospect_prep') {
            // Messaging Defense — generate questions from company profile
            setProspectPrepLoading(true);
            try {
                await meetingStore.initProspectPrepSession(companyStore.company.id, 5);
                history.push('/record-presentation/messaging-defense');
            } catch (err) {
                setError(err.message || 'Failed to initialize Messaging Defense session');
                setProspectPrepLoading(false);
            }
        } else {
            try {
                await meetingStore.setMeetingType(recordingType, companyStore.company.id);
                history.push(`/record-presentation/${recordingType}`);
            } catch (err) {
                setError(err.message || 'Failed to start the session');
            }
        }
    };

    const handleDocumentQA = async (file) => {
        const companyId = companyStore.company.id;

        // 1. Get presigned upload URL
        const filename = `${Date.now()}-${file.name}`;
        // Use file.type if available; fall back to application/octet-stream to ensure
        // the presigned URL signature and the S3 PUT Content-Type always match.
        const contentType = file.type || 'application/octet-stream';
        const headers = { 'Content-Type': contentType };
        const uploadResult = await axios.post(
            `/companies/${companyId}/uploads/doc-qa%2F${encodeURIComponent(filename)}`,
            {},
            { headers }
        );
        const uploadUrl = uploadResult.data.uploadUrl;
        const documentKey = uploadResult.data.key;

        // 2. Upload file to S3
        await axios.put(uploadUrl, file, { headers, baseURL: '' });

        // 3. Initialize document QA session
        await meetingStore.initDocumentQASession(companyId, 5, documentKey);

        // Navigate directly - no need to update modal state since the component unmounts
        history.push('/record-presentation/document-defense');
    };

    const renderCard = (recordingType) => (
        <Grid.Column key={recordingType.recordingKey} className={`${recordingType.recordingKey.replace('_', '-')}`}>
            <div className='ui card' style={{ border: 'solid 2px #dddddd', padding: 20, borderRadius: 5, minWidth: 230, height: '100%', gap: 8 }}>
                {recordingType.disabled && <ComingSoonBanner />}
                <div>
                    <img src={recordingType.icon} className='recording-card-icon' alt={recordingType.type} />
                </div>
                <div style={{ display: 'flex', alignItems: 'center' }}>
                    <h2 style={{ display: 'inline-block', marginRight: '10px' }}>
                        {recordingType.type}
                        {recordingType.description && <InfoPopup popupContent={recordingType.description} />}
                    </h2>
                </div>
                <button
                    disabled={recordingType.disabled}
                    className={`start-recording-button btn-${recordingType.recordingKey.split('_')[0]}`}
                    onClick={() => onRecordingTypeClick(recordingType.recordingKey)}
                >
                    <Icon name='video' />
                    {recordingType.disabled ? 'Coming Soon' : 'Start Recording'}
                </button>
                <span></span> {/* This is a hack to make the button have rounded corners */}
            </div>
        </Grid.Column>
    );

    const renderCards = () => {
        // Group recordings by level
        const level1Recordings = meetingTypes.filter(type => type.level === 1);
        const level2Recordings = meetingTypes.filter(type => type.level === 2);
        const level3Recordings = meetingTypes.filter(type => type.level === 3);
        const level4Recordings = meetingTypes.filter(type => type.level === 4);

        return (
            <div className='record-card'>
                <Grid container stackable>
                    <Grid.Row>
                        <Grid.Column width={16}>
                            <Header as='h2'>Pitch Training</Header>
                            <Divider />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns="equal">
                        {level1Recordings.map(renderCard)}
                    </Grid.Row>

                    <Grid.Row style={{ marginTop: '2em' }}>
                        <Grid.Column width={16}>
                            <Header as='h2'>Interactive Pitch Training</Header>
                            <Divider />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns="equal">
                        {level2Recordings.map(renderCard)}
                    </Grid.Row>

                    <Grid.Row style={{ marginTop: '2em' }}>
                        <Grid.Column width={16}>
                            <Header as='h2'>Interactive Defensive Training</Header>
                            <Divider />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns={3}>
                        {level3Recordings.map(renderCard)}
                    </Grid.Row>

                    {companyStore.company.investorPitchEnabled && (<>
                    <Grid.Row style={{ marginTop: '2em' }}>
                        <Grid.Column width={16}>
                            <Header as='h2'>Investor Pitch Training</Header>
                            <Divider />
                        </Grid.Column>
                    </Grid.Row>
                    <Grid.Row columns="equal">
                        {level4Recordings.map(renderCard)}
                    </Grid.Row>
                    </>)}
                </Grid>
            </div>
        );
    };

    return <>
        <div className='record-presentation'>
            <div style={{ textAlign: 'center', width: '100%', fontWeight: 600, color: '#4c4d50' }}>Choose the type of presentation to get started. Make sure you are prepared and facing the camera.
                {error && <ErrorDisplay message={error} />}
            </div>
            {renderCards()}
        </div>

        <DocumentUploadModal
            open={showDocumentModal}
            onClose={() => setShowDocumentModal(false)}
            onDocumentQA={handleDocumentQA}
        />

        <ProspectPrepLoadingModal open={prospectPrepLoading} />
    </>;
};

export default observer(RecordPresentationSelection);
